import React, { useState, useEffect } from "react";
import { Modal, Button, Label, TextInput } from "flowbite-react";
import { useTranslation } from "react-i18next";

interface Props {
  show: boolean;
  onClose: () => void;
  onSubmit: (opts: { start: Date; durationMinutes: number; commType: string }) => void;
  defaultDurationMinutes?: number;
  commType: string; // "audio" | "audio+video"
}

const ScheduleCallModal: React.FC<Props> = ({ show, onClose, onSubmit, defaultDurationMinutes = 30, commType }) => {
  const { t } = useTranslation("chat");
  const [dateTimeLocal, setDateTimeLocal] = useState<string>("");
  const [duration, setDuration] = useState<number>(defaultDurationMinutes);
  const [minDateTime, setMinDateTime] = useState<string>("");
  const [dateLocal, setDateLocal] = useState<string>("");
  const [hourLocal, setHourLocal] = useState<string>("");
  const [minuteLocal, setMinuteLocal] = useState<string>("");

  useEffect(() => {
    if (show) {
      // default to 5 minutes from now rounded to next 5 minutes
      const now = new Date();
      now.setMinutes(now.getMinutes() + 5 - (now.getMinutes() % 5));
      const isoLocal = new Date(now.getTime() + now.getTimezoneOffset() * 60000).toISOString().slice(0, -1);
      setDateTimeLocal(isoLocal.substring(0, 16));
      // split into date and time parts for separate inputs
      setDateLocal(isoLocal.substring(0, 10));
      setHourLocal(isoLocal.substring(11, 13));
      setMinuteLocal(isoLocal.substring(14, 16));
      // set min value to current local time (no past selection)
      const nowForMin = new Date();
      const isoLocalNow = new Date(nowForMin.getTime() + nowForMin.getTimezoneOffset() * 60000).toISOString().slice(0, -1);
      setMinDateTime(isoLocalNow.substring(0, 16));
      setDuration(defaultDurationMinutes);
    }
  }, [show, defaultDurationMinutes]);

  const sanitizeTimeSelection = (d: string, h: string, m: string) => {
    if (!d || h === "" || m === "") {
      return { date: d, hour: h, minute: m };
    }

    const minDate = minDateTime.substring(0, 10);
    const minHour = Number(minDateTime.substring(11, 13));
    const minMinute = Number(minDateTime.substring(14, 16));
    let hour = Number(h);
    let minute = Number(m);

    if (d === minDate) {
      if (hour < minHour) {
        hour = minHour;
        minute = Math.max(minute, minMinute);
      }
      if (hour === minHour && minute < minMinute) {
        minute = minMinute;
      }
    }

    return {
      date: d,
      hour: String(hour).padStart(2, "0"),
      minute: String(minute).padStart(2, "0"),
    };
  };

  // When date/hour/minute changes, update combined datetime and clamp if needed
  const updateCombinedDateTime = (d: string, h: string, m: string) => {
    if (!d || h === "" || m === "") {
      setDateTimeLocal("");
      return;
    }

    const safe = sanitizeTimeSelection(d, h, m);
    if (safe.hour !== h) setHourLocal(safe.hour);
    if (safe.minute !== m) setMinuteLocal(safe.minute);

    const combined = `${safe.date}T${safe.hour}:${safe.minute}`;
    const selected = new Date(combined);
    const min = new Date(minDateTime || 0);
    if (minDateTime && selected.getTime() < min.getTime()) {
      // clamp to minDateTime
      setDateTimeLocal(minDateTime);
      setDateLocal(minDateTime.substring(0, 10));
      setHourLocal(minDateTime.substring(11, 13));
      setMinuteLocal(minDateTime.substring(14, 16));
    } else {
      setDateTimeLocal(combined);
    }
  };

  const handleSubmit = () => {
    if (!dateTimeLocal) return;
    const local = new Date(dateTimeLocal);
    onSubmit({ start: local, durationMinutes: duration, commType });
    onClose();
  };

  return (
    <Modal show={show} size="md" onClose={onClose}>
      <Modal.Body>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">{t("scheduleCall")}</h3>
          <div>
            <div className="mb-2 block">
              <Label htmlFor="datetime">{t("startTime")}</Label>
            </div>
            <div className="flex gap-2">
              <input
                id="date"
                type="date"
                className="form-input rounded-md border px-3 py-2"
                value={dateLocal}
                min={minDateTime ? minDateTime.substring(0, 10) : undefined}
                onChange={(e) => {
                  const nextDate = e.target.value;
                  setDateLocal(nextDate);
                  updateCombinedDateTime(nextDate, hourLocal, minuteLocal);
                }}
              />
              <select
                id="hour"
                className="form-select rounded-md border px-3 py-2"
                value={hourLocal}
                onChange={(e) => {
                  const nextHour = e.target.value;
                  setHourLocal(nextHour);
                  updateCombinedDateTime(dateLocal, nextHour, minuteLocal);
                }}
              >
                {Array.from({ length: 24 }).map((_, h) => {
                  const value = String(h).padStart(2, "0");
                  const isPastHourToday =
                    dateLocal === minDateTime.substring(0, 10) &&
                    Number(value) < Number(minDateTime.substring(11, 13));

                  return (
                    <option key={value} value={value} disabled={isPastHourToday}>
                      {value}
                    </option>
                  );
                })}
              </select>
              <select
                id="minute"
                className="form-select rounded-md border px-3 py-2"
                value={minuteLocal}
                onChange={(e) => {
                  const nextMinute = e.target.value;
                  setMinuteLocal(nextMinute);
                  updateCombinedDateTime(dateLocal, hourLocal, nextMinute);
                }}
              >
                {Array.from({ length: 60 }).map((_, m) => {
                  const value = String(m).padStart(2, "0");
                  const isCurrentMinHourToday =
                    dateLocal === minDateTime.substring(0, 10) &&
                    Number(hourLocal) === Number(minDateTime.substring(11, 13));
                  const isPastMinute = isCurrentMinHourToday && Number(value) < Number(minDateTime.substring(14, 16));

                  return (
                    <option key={value} value={value} disabled={isPastMinute}>
                      {value}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div>
            <div className="mb-2 block">
              <Label htmlFor="duration">{t("duration")}</Label>
            </div>
            <TextInput
              id="duration"
              type="number"
              min={5}
              step={5}
              value={String(duration)}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button color="gray" onClick={onClose}>{t("cancel")}</Button>
            <Button onClick={handleSubmit} disabled={!dateTimeLocal}>{t("schedule")}</Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ScheduleCallModal;
